import { inject, Injectable } from "@angular/core";
import { Constants } from "@app/core/constants";
import { ConfigParamsAndScreenTextsService } from "@app/core/services/config-params-and-screen-texts.service";
import { CaciUtil } from "@app/core/util/caci-util";
import { LanguageService } from "@app/shared/services/language.service";
import { Plan } from "@core/domain/plan";
import { Period, PlanDetails, StudyYear } from "@core/domain/plan-details";
import { PlanLater, Specialisation } from "@core/domain/plan-later";
import { TranslateService } from "@ngx-translate/core";
import { SCREENTEXT_MINORS } from "../config-param-or-screen-text";
import { Course } from "../course";
import { CourseTest } from "../course-test";
import { ExamComponentForPlanCourse, ExamComponentType } from "../exam-component";
import {
  ProposalBlocksExamComponents,
  ProposalDocumentUpload,
  ProposalReviewMoment,
  ProposalStatus,
} from "../proposal-status";

@Injectable({
  providedIn: "root",
})
export class PlanHelper {
  private readonly translate = inject(TranslateService);
  private readonly languageService = inject(LanguageService);
  private readonly configParamsAndScreenTextsService = inject(ConfigParamsAndScreenTextsService);

  getPlanType(plan: Plan): string {
    return this.translate.instant(plan.isDraft ? "label.draft" : "label.plan");
  }

  getPlanTitle(plan: Plan): string {
    return `${this.getPlanType(plan)}: ${plan.name}`;
  }

  getPlanYear(plan: PlanDetails, yearNr: number): StudyYear {
    return plan.studyYears.find((year) => year.yearNr === yearNr)!;
  }

  getPlanningYears(plan: PlanDetails): number[] {
    const planningYears = plan.studyYears
      .filter((year) => year.academicYear >= plan.currentYear)
      .map((year) => year.academicYear);

    if (plan.currentYear === Math.max(...planningYears)) {
      planningYears.push(plan.currentYear + 1);
    }

    return planningYears;
  }

  getPastYears(plan: PlanDetails): number[] {
    return plan.studyYears.filter((year) => year.academicYear < plan.currentYear).map((year) => year.academicYear);
  }

  getPlanPeriod(periods: Period[], periodNr: number): Period {
    return periods.find((period) => period.periodNr === periodNr)!;
  }

  isUploadPossible(plan: PlanDetails): boolean {
    return plan?.proposalDocumentToUpload !== ProposalDocumentUpload.NOT_ALLOWED;
  }

  /* Is the proposal status locked because it is submitted, has positive advice or has final approval? */
  proposalStatusIsLocked(proposalStatus: ProposalStatus, proposalReviewMoment: ProposalReviewMoment): boolean {
    return (
      proposalStatus === ProposalStatus.SUBMITTED_FOR_APPROVAL ||
      proposalStatus === ProposalStatus.POSITIVE_ADVICE ||
      (proposalStatus === ProposalStatus.APPROVED && proposalReviewMoment === ProposalReviewMoment.FINAL)
    );
  }

  planHasLockedProposal(plan: PlanDetails): boolean {
    return this.proposalStatusIsLocked(plan.proposalStatus, plan.proposalReviewMoment);
  }

  /* Is this component locked because it is part of a proposal that is locked? */
  isLockedProposalComponent(plan: PlanDetails, examComponent: ExamComponentForPlanCourse): boolean {
    const componentIsInElectives =
      examComponent.type === ExamComponentType.ELECTIVES || examComponent.minorProgram !== "";
    return (
      this.planHasLockedProposal(plan) &&
      ((plan.proposalBlocksExamComponents === ProposalBlocksExamComponents.ELECTIVES && componentIsInElectives) ||
        (plan.proposalBlocksExamComponents === ProposalBlocksExamComponents.ALL &&
          !this.isExamComponentOther(examComponent)))
    );
  }

  getLockedProposalExplanation(
    plan: PlanDetails,
    planLater: PlanLater,
    examComponents: ExamComponentForPlanCourse[],
  ): string {
    if (!this.planHasLockedProposal(plan)) {
      return "";
    }
    let lockedElectivesComponentPresent = false;
    examComponents.forEach((examComponent) => {
      if (this.isLockedProposalComponent(plan, examComponent)) {
        lockedElectivesComponentPresent =
          lockedElectivesComponentPresent || examComponent.type === ExamComponentType.ELECTIVES;
      }
    });
    const minorsPresentInPlanLater = planLater.electives.minors && planLater.electives.minors.length > 0;
    if (lockedElectivesComponentPresent && !minorsPresentInPlanLater) {
      return this.translate.instant("changeExamComponent.lockedProposalExplanationElectives", {
        electivesName: plan.electivesName,
      });
    }
    const minorsText = this.configParamsAndScreenTextsService.getConfigParamOrScreenText(SCREENTEXT_MINORS);
    if (lockedElectivesComponentPresent && minorsPresentInPlanLater) {
      return this.translate.instant("changeExamComponent.lockedProposalExplanationElectivesMinors", {
        electivesName: plan.electivesName,
        minors: minorsText,
      });
    }
    if (minorsPresentInPlanLater) {
      return this.translate.instant("changeExamComponent.lockedProposalExplanationMinors", {
        minors: minorsText,
      });
    }
    return "";
  }

  isExamComponentOther(examComponent: ExamComponentForPlanCourse): boolean {
    return examComponent.examComponentCode === Constants.EXAM_COMPONENT_OTHER;
  }

  addUniqueIdsToExamComponents(examComponentsToChooseFrom: ExamComponentForPlanCourse[]): void {
    examComponentsToChooseFrom.forEach((examComponent) => {
      examComponent.uniqueId = this.createUniqueIdExamComponent(
        examComponent.examComponentCode,
        examComponent.studyProgram,
        examComponent.specialisationProgram,
        examComponent.minorProgram,
      );
    });
  }

  private createUniqueIdExamComponent(
    examComponentCode: string,
    studyProgram: string,
    specialisationProgram: string | undefined = "",
    minorProgram: string | undefined = "",
  ): string {
    return `${examComponentCode}#${studyProgram}#${specialisationProgram}#${minorProgram}`;
  }

  findExamComponentThatMatchesCourse(
    examComponentsToChooseFrom: ExamComponentForPlanCourse[],
    course: Course,
  ): ExamComponentForPlanCourse | undefined {
    const uniqueIdFromCourse = this.createUniqueIdExamComponent(
      course.examComponentCode,
      course.studyProgram,
      course.specialisationProgram,
      course.minorProgram,
    );
    const foundExamComponent = examComponentsToChooseFrom.find(
      (examComponent) => examComponent.uniqueId === uniqueIdFromCourse,
    );
    if (!foundExamComponent) {
      return examComponentsToChooseFrom.find(
        (examComponentToChoose) => examComponentToChoose.examComponentCode === Constants.EXAM_COMPONENT_OTHER,
      );
    }
    return foundExamComponent;
  }

  getExamComponentTypeLabel(type?: string, minimumPoints?: number): string | undefined {
    switch (type) {
      case ExamComponentType.MANDATORY:
        return this.translate.instant("examComponent.mandatory");
      case ExamComponentType.MANDATORY_CHOICE:
        return this.translate.instant("examComponent.mandatoryChoice", {
          minimumPoints,
        });
      case ExamComponentType.CHOICE:
        return this.translate.instant("examComponent.choice");
      default:
        return undefined;
    }
  }

  specialisationEquals(specialisation1: Specialisation, specialisation2: Specialisation): boolean {
    return (
      specialisation1.name === specialisation2.name &&
      specialisation1.code === specialisation2.code &&
      specialisation1.studyProgramCode === specialisation2.studyProgramCode
    );
  }

  /**
   * Only show courses according to the following algorithm:
   * User is allowed to see courses with the same or lower examenfase.
   * ExamStage priority:
   * - 1e-jaar-bachelor
   * - associate-degree
   * - bachelor
   * - pre-master
   * - master
   * - empty (always shown)
   *
   * NOTE: If tweede_examenfase is set, it is leading.
   */
  determineExamStages(plan: PlanDetails): string[] {
    const examPhases: string[] = [];
    if (plan !== undefined) {
      examPhases.push(...["master", "pre-master", "bachelor", "associate-degree", "1e-jaar-bachelor", ""]);
      const examStage = plan.secondExamStage?.length ? plan.secondExamStage : plan.examStage;

      switch (examStage) {
        case "master":
          // use set of examStages which is already created
          break;
        case "pre-master":
          examPhases.splice(0, 1);
          break;
        case "bachelor":
          examPhases.splice(0, 2);
          break;
        case "associate-degree":
          examPhases.splice(0, 3);
          break;
        case "1e-jaar-bachelor":
          examPhases.splice(0, 4);
          break;
        default:
          // No valid exam phase found
          examPhases.splice(0, 5);
          break;
      }
    } else {
      examPhases.push("");
    }

    return examPhases;
  }

  determineExamTypeCode(plan: PlanDetails): string[] {
    const examentypeCode: string[] = [];
    if (plan !== undefined) {
      if (plan.secondExamTypeCode?.length) {
        examentypeCode.push(plan.secondExamTypeCode);
      } else if (plan.examTypeCode?.length) {
        examentypeCode.push(plan.examTypeCode);
      }
    }

    return examentypeCode;
  }

  calculatePointsPlanned(plan: PlanDetails | undefined): string {
    if (!plan) {
      return "0";
    }

    // Don't count the ribbon repeat courses (do count the 1st ribbon course), to prevent doubling points for a ribbon course
    const pointsPlanned = plan.studyYears
      .flatMap((year) => year.periods)
      .flatMap((period) => period.courses)
      .filter((course) => !course.isRibbonRepeat)
      .reduce((total, course) => total + course.studyPoints, 0);

    return CaciUtil.getStudyPointsDisplay(pointsPlanned, this.languageService.getLanguage());
  }

  stripSpaces(text: string): string {
    return text?.replace(/\s/g, "");
  }

  addCourseTests(period: Period): void {
    if (period.courseTests?.length) {
      const testsPerCourseCode: {
        [courseCode: string]: { courseName: string; courseTests: CourseTest[] };
      } = {};

      period.courseTests.forEach((courseTest) => {
        const courseCode = courseTest.courseCode;
        const courseName = courseTest.courseName;
        if (testsPerCourseCode[courseCode]) {
          testsPerCourseCode[courseCode].courseTests.push(courseTest);
        } else {
          testsPerCourseCode[courseCode] = { courseName: courseName, courseTests: [courseTest] };
        }
      });

      // Sort the courseTests for each courseCode by testDescription
      Object.keys(testsPerCourseCode).forEach((courseCode) => {
        testsPerCourseCode[courseCode].courseTests.sort((a, b) => a.testDescription.localeCompare(b.testDescription));
      });

      period.courses.forEach((course) => {
        const courseCode = course.code;
        if (courseCode && testsPerCourseCode[courseCode]) {
          // add test list to course object
          course.courseTests = testsPerCourseCode[courseCode].courseTests;
          // remove from testsPerCourseCode, so that the tests for this course are not added as courseTestBlocks
          delete testsPerCourseCode[courseCode];
        }
      });

      // the remaining tests, for courses that don't occur in this period, are added as courseTestBlocks
      period.courseTestBlocks = Object.keys(testsPerCourseCode)
        .map((courseCode) => ({
          courseCode,
          courseName: testsPerCourseCode[courseCode].courseName,
          courseTests: testsPerCourseCode[courseCode].courseTests,
        }))
        .sort((a, b) => a.courseCode.localeCompare(b.courseCode));
    }
  }

  refreshCourseTests(period: Period): void {
    period.courses.forEach((course) => {
      course.courseTests = [];
    });
    period.courseTestBlocks = [];
    this.addCourseTests(period);
  }
}
